import { deleteDoc, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { CashFlow, cashFlowsRef, NewCashFlow, stampNewCashFlow } from '@api/CashFlows/CashFlow';
import UserServ from '@api/Users/UserServ';
import store from '@store/index';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeDeletedCashFlow, describePurgedOrderCashFlows } from '@api/ActivityLogs/CashActivity';
import { getCashRegisterMillis } from '@utils/CashRegisters';
import { isExpenseFlow } from '@utils/CashFlows';
import CashRegisterServ from '@api/CashRegister/CashRegisterServ';
import { CashRegister } from '@api/CashRegister/CashRegister';
export default {
    /**
     * ⚠️ **Crear un movimiento NO escribe bitácora, a propósito** (D6): el
     * documento ya guarda `userId` y `date`, o sea quién y cuándo. Registrarlo
     * otra vez sería duplicar un rastro que ya existe — y son ~200 movimientos al
     * día entre las diez sucursales. Lo que SÍ deja renglón es **borrarlo**, que
     * es donde el rastro desaparece con el documento.
     *
     * Es **UNA escritura**: `doc(ref)` genera el id **en el cliente, sin ir a la
     * red**, así que el documento nace completo. Antes eran dos (`addDoc` + un
     * `updateDoc` cuyo único fin era meter dentro el id recién generado) — la
     * misma corrección que ya se le hizo a `addActivityLog` el 2026-09-10.
     */
    async saveCashFlow(
        newCashFlow: NewCashFlow,
        options: { branchOfficeId?: string; id?: string } = {}
    ): Promise<CashFlow> {
        const user = await UserServ.getUser();
        // `id` fijo: la instancia de un gasto fijo (`{plantilla}_{yyyy-MM}`), para que
        // registrarla dos veces pise el mismo documento. `branchOfficeId`: el admin que
        // registra desde el Informe la renta de otra sucursal.
        const ref = options.id ? doc(cashFlowsRef, options.id) : doc(cashFlowsRef);
        const cashFlow = stampNewCashFlow(newCashFlow, {
            id: ref.id,
            userId: user.uid,
            branchOfficeId: options.branchOfficeId || store.getState().auth.branchOfficeSelected?.id,
            now: new Date()
        });
        await setDoc(ref, cashFlow);
        return cashFlow;
    },
    // branchOfficeId opcional: sin él usa la sucursal en operación (comportamiento
    // original); 'all' = sin filtro de sucursal (para admin en informe de ventas).
    async getCashFlows(startDate: Date, endDate: Date, branchOfficeId?: string): Promise<CashFlow[]> {
        const cashFlow: CashFlow[] = [];
        const effectiveBranchId = branchOfficeId ?? store.getState().auth.branchOfficeSelected?.id;
        const filters = [where('date', '>=', startDate), where('date', '<', endDate)];
        if (effectiveBranchId !== 'all') {
            filters.push(where('branchOfficeId', '==', effectiveBranchId));
        }
        const queryDoc = query(cashFlowsRef, ...filters);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return cashFlow;
        }
        snapshot.docs.map((doc: any) => {
            cashFlow.push(doc.data() as CashFlow);
        });
        return cashFlow;
    },
    async getCashFlow(cashFlowId: string): Promise<CashFlow> {
        const documentData = await getDoc(doc(cashFlowsRef, cashFlowId));
        return documentData.data() as CashFlow;
    },
    /**
     * Borra un movimiento del corte **dejando constancia** (M3 de la bitácora).
     *
     * ## Dónde queda el renglón, y por qué ahí
     * Un `cash-flow` no tiene pantalla propia que se pueda abrir, así que colgarle
     * la bitácora a él sería escribir en un lugar que nadie puede leer — lo que
     * D5 prohíbe. Se cuelga de la **CAJA cuya ventana contiene ese movimiento**,
     * que es el objeto que un admin abre cuando un corte no cuadra, y es
     * exactamente la pregunta que este renglón contesta.
     *
     * Si el movimiento no cae en ninguna caja (se registró sin caja abierta: el
     * `outside` del panel) no hay dónde colgarlo y **no se escribe** — antes que
     * inventar un destino, se prefiere no registrar. Es raro y está medido: el
     * panel de cajas ya reporta esos casos aparte.
     *
     * El renglón lleva monto, tipo y descripción DENTRO del texto porque el
     * documento se va: es lo único que va a quedar.
     *
     * Costo: 1 lectura del movimiento + 1 consulta de cajas abiertas/del día + 1
     * escritura, y solo cuando alguien borra a mano (unas pocas veces por semana).
     */
    async deleteCashFlow(cashFlowId: string): Promise<void> {
        let cashFlow: CashFlow | undefined;
        try {
            cashFlow = await this.getCashFlow(cashFlowId);
        } catch {
            // Si no se pudo leer, se borra igual: la bitácora nunca bloquea.
        }
        // 🚨 Un GASTO nunca se borra (plan de Gastos §7): se cancela con motivo y PIN
        // (`ExpenseServ.cancelExpense`) y queda a la vista. Aquí solo se borran otros ingresos.
        if (cashFlow && isExpenseFlow(cashFlow)) {
            throw new Error('Un gasto no se borra: se cancela desde la pestaña Gastos del Informe');
        }
        await deleteDoc(doc(cashFlowsRef, cashFlowId));
        if (!cashFlow) return;
        try {
            const cajaId = await this.findCashRegisterForFlow(cashFlow);
            if (cajaId) {
                await ActivityLogServ.addActivityLog('cashRegister', cajaId, describeDeletedCashFlow(cashFlow));
            }
        } catch {
            // Observar nunca puede ser la causa de que algo no pase.
        }
    },
    /**
     * La caja a la que pertenece un movimiento: la de su sucursal cuya ventana
     * [apertura, cierre) lo contiene. Es el MISMO criterio de reparto que usa el
     * corte (`@utils/CashRegisters`) — si aquí se usara otro, el renglón aparecería
     * en una caja distinta de aquella en la que el dinero se ve faltar.
     */
    async findCashRegisterForFlow(cashFlow: CashFlow): Promise<string | undefined> {
        return (await this.findCashRegisterOfFlow(cashFlow))?.id;
    },
    /** La caja completa (con `closedAt`): editar un gasto pregunta si su corte ya cerró. */
    async findCashRegisterOfFlow(cashFlow: CashFlow): Promise<CashRegister | undefined> {
        const millis = getCashRegisterMillis(cashFlow.date);
        if (!millis || !cashFlow.branchOfficeId) return undefined;
        const registers = await CashRegisterServ.getCashRegistersByRange(
            new Date(millis - 24 * 60 * 60 * 1000),
            new Date(millis + 24 * 60 * 60 * 1000),
            cashFlow.branchOfficeId
        );
        const candidatas = registers
            .filter((register) => register.branchOfficeId === cashFlow.branchOfficeId)
            .sort((a, b) => getCashRegisterMillis(b.date) - getCashRegisterMillis(a.date));
        const contenedora = candidatas.find((register) => {
            const desde = getCashRegisterMillis(register.date);
            if (!desde || millis < desde) return false;
            const hasta = register.closedAt ? getCashRegisterMillis(register.closedAt) : undefined;
            return !hasta || millis < hasta;
        });
        return contenedora;
    },
    /**
     * Los movimientos que generó una orden al completarse (ingreso en efectivo).
     * Al purgar la orden hay que borrarlos o el corte queda descuadrado con un
     * ingreso sin pedido que lo respalde.
     *
     * ⚠️ **Y eso deja un hueco en la CAJA que hay que registrar** (M3, 2026-09-11):
     * el purgado deja su renglón en la bitácora del PEDIDO… que se acaba de borrar.
     * Así que el corte perdía dinero y en la caja no quedaba ni una línea diciendo
     * por qué. Aquí se escribe ese renglón, en la caja a la que pertenecían los
     * movimientos — que es donde alguien va a ver el faltante.
     */
    async deleteCashFlowsByOrderId(orderId: string): Promise<number> {
        const queryDoc = query(cashFlowsRef, where('orderId', '==', orderId));
        const snapshot = await getDocs(queryDoc);
        const borrados = snapshot.docs.map((document) => document.data() as CashFlow);
        await Promise.all(snapshot.docs.map((document) => deleteDoc(document.ref)));
        try {
            await this.logPurgedCashFlows(borrados);
        } catch {
            // La bitácora observa: jamás puede impedir que se purgue un pedido.
        }
        return snapshot.size;
    },
    /**
     * Un renglón POR CAJA afectada (no uno por movimiento): lo que importa para
     * el corte es cuánto salió de esa caja, no el desglose de una orden que ya no
     * existe. Un pedido genera 1 movimiento en la práctica, así que casi siempre
     * es una caja y una escritura.
     */
    async logPurgedCashFlows(cashFlows: CashFlow[]): Promise<void> {
        if (cashFlows.length === 0) return;
        const porCaja = new Map<string, number>();
        for (const cashFlow of cashFlows) {
            const cajaId = await this.findCashRegisterForFlow(cashFlow);
            if (!cajaId) continue;
            porCaja.set(cajaId, (porCaja.get(cajaId) || 0) + (cashFlow.amount || 0));
        }
        await Promise.all(
            Array.from(porCaja.entries()).map(([cajaId, monto]) =>
                ActivityLogServ.addActivityLog('cashRegister', cajaId, describePurgedOrderCashFlows(monto))
            )
        );
    }
};
